import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { toPersianDigits } from '../lib/date'

/* ------------------------------------------------------------------
   Tabs
   ------------------------------------------------------------------ */

export interface TabItem {
  key: string
  label: ReactNode
  count?: number
  disabled?: boolean
}

export interface TabsProps {
  items: TabItem[]
  value: string
  onChange: (key: string) => void
  className?: string
}

export function Tabs({ items, value, onChange, className }: TabsProps) {
  return (
    <div
      role="tablist"
      className={cn('flex gap-1 overflow-x-auto border-b border-[var(--pf-border)]', className)}
    >
      {items.map((item) => {
        const active = item.key === value
        return (
          <button
            key={item.key}
            role="tab"
            type="button"
            aria-selected={active}
            disabled={item.disabled}
            onClick={() => onChange(item.key)}
            className={cn(
              'relative flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3.5 py-2.5',
              'text-[length:var(--pf-text-sm)] font-[var(--pf-weight-medium)]',
              'transition-colors duration-[var(--pf-duration-fast)]',
              'disabled:cursor-not-allowed disabled:opacity-50',
              /* متن تب فعال تیره می‌ماند؛ نشانه‌گر فعال‌بودن، خط
                 سبز زیر آن است — نه رنگ متن. */
              active
                ? 'text-[var(--pf-text)]'
                : 'text-[var(--pf-text-muted)] hover:text-[var(--pf-text)]',
            )}
          >
            {item.label}
            {item.count != null && item.count > 0 && (
              <span
                className={cn(
                  'rounded-[var(--pf-radius-full)] px-1.5 py-0.5 text-[length:var(--pf-text-2xs)] pf-tnum',
                  active
                    ? 'bg-[var(--pf-primary-soft)] text-[var(--pf-accent-700)]'
                    : 'bg-[var(--pf-surface-muted)] text-[var(--pf-text-muted)]',
                )}
              >
                {toPersianDigits(item.count)}
              </span>
            )}
            {active && (
              <span
                /* --pf-link — همان دلیل نوار تب: نشانگر باریک روی سطح روشن */
                className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-[var(--pf-link)]"
                aria-hidden="true"
              />
            )}
          </button>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------
   Pagination — بند ۱۵: Unbounded List ممنوع است
   ------------------------------------------------------------------ */

export interface PaginationProps {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]
  className?: string
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 50, 100],
  className,
}: PaginationProps) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 px-1 py-3',
        'text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <span className="pf-tnum">
          نمایش {toPersianDigits(from)}–{toPersianDigits(to)} از {toPersianDigits(total)}
        </span>

        {onPageSizeChange && (
          <label className="flex items-center gap-1.5">
            <span>در هر صفحه</span>
            <select
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              className="h-9 rounded-[var(--pf-radius-sm)] border border-[var(--pf-border-strong)] bg-[var(--pf-surface)] px-1.5 text-[length:var(--pf-text-xs)] text-[var(--pf-text)] lg:h-7"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/*
        ⚠️ شماره‌های صفحه به‌جای متنِ «۲ / ۱».

        🔴 با متنِ تنها، پرش به صفحهٔ دلخواه ممکن نبود: کاربر برای رسیدن
        به صفحهٔ ۵ باید چهار بار «بعدی» می‌زد. حالا شماره‌ها دکمه‌اند.

        ⚠️ فهرست با «…» فشرده می‌شود؛ در فهرستی با ۴۰ صفحه، ۴۰ دکمه
        خودش یک مشکل تازه است.
      */}
      <div className="flex items-center gap-1">
        <PageButton
          label="صفحه قبل"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          direction="prev"
        />

        {pageWindow(page, lastPage).map((item, index) =>
          item === null ? (
            <span
              key={`gap-${index}`}
              aria-hidden="true"
              className="px-1 text-[var(--pf-text-subtle)]"
            >
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => onPageChange(item)}
              aria-label={`صفحه ${toPersianDigits(item)}`}
              aria-current={item === page ? 'page' : undefined}
              className={cn(
                'pf-tnum flex h-7 min-w-7 items-center justify-center rounded-[var(--pf-radius-sm)] px-1.5',
                'transition-colors duration-[var(--pf-duration-fast)]',
                item === page
                  ? 'bg-[var(--pf-primary)] font-[var(--pf-weight-semibold)] text-[var(--pf-on-primary)]'
                  : 'text-[var(--pf-text)] hover:bg-[var(--pf-surface-hover)]',
              )}
            >
              {toPersianDigits(item)}
            </button>
          ),
        )}

        <PageButton
          label="صفحه بعد"
          disabled={page >= lastPage}
          onClick={() => onPageChange(page + 1)}
          direction="next"
        />
      </div>
    </div>
  )
}

/**
 * پنجرهٔ شماره‌های صفحه — `null` یعنی «…».
 *
 * قاعده: همیشه صفحهٔ اول و آخر، به‌علاوهٔ یک صفحه پیش و پس از صفحهٔ
 * جاری. بقیه فشرده می‌شوند.
 *
 * ⚠️ تا ۷ صفحه اصلاً فشرده نمی‌شود؛ «…» برای فهرستِ کوتاه فقط یک
 * پرشِ بی‌دلیل در شماره‌هاست بی‌آنکه جایی صرفه‌جویی کند.
 */
function pageWindow(page: number, last: number): (number | null)[] {
  if (last <= 7) {
    return Array.from({ length: last }, (_, index) => index + 1)
  }

  const pages = new Set([1, last, page, page - 1, page + 1])
  const sorted = [...pages].filter((value) => value >= 1 && value <= last).sort((a, b) => a - b)

  const output: (number | null)[] = []

  sorted.forEach((value, index) => {
    /* اگر بین این شماره و قبلی فاصله افتاده، یک «…» می‌گذاریم. */
    if (index > 0 && value - sorted[index - 1] > 1) output.push(null)

    output.push(value)
  })

  return output
}

function PageButton({
  label,
  disabled,
  onClick,
  direction,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  direction: 'prev' | 'next'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'flex size-7 items-center justify-center rounded-[var(--pf-radius-sm)]',
        'border border-[var(--pf-border-strong)] text-[var(--pf-text)]',
        'transition-colors duration-[var(--pf-duration-fast)]',
        'hover:bg-[var(--pf-surface-hover)] disabled:opacity-40 disabled:pointer-events-none',
      )}
    >
      <svg
        viewBox="0 0 12 12"
        fill="none"
        className={cn('size-3', direction === 'next' ? 'rotate-180' : '', 'rtl:-scale-x-100')}
        aria-hidden="true"
      >
        <path
          d="m7.5 2.5-3.5 3.5 3.5 3.5"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  )
}

/* ------------------------------------------------------------------
   Avatar
   ------------------------------------------------------------------ */

export interface AvatarProps {
  name: string
  src?: string | null
  size?: 'xs' | 'sm' | 'md' | 'lg'
  className?: string
  /**
   * تصویر پیش‌فرض وقتی کاربر عکسی ندارد — جای حروف اول می‌نشیند.
   * ⚠️ مثل EmptyState نشانی می‌گیرد و خودش مسیر نمی‌سازد: ui-kit نباید
   * به فایل‌های داخل اپ وب گره بخورد.
   */
  fallbackSrc?: string
}

const AVATAR_SIZES = {
  xs: 'size-6 text-[length:var(--pf-text-2xs)]',
  sm: 'size-8 text-[length:var(--pf-text-xs)]',
  md: 'size-9.5 text-[length:var(--pf-text-sm)]',
  lg: 'size-12 text-[length:var(--pf-text-base)]',
}

/** رنگ پس‌زمینه پایدار بر مبنای نام — بدون تصادف */
function hueFromName(name: string): number {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) % 360
  }
  return hash
}

export function Avatar({ name, src, size = 'md', className, fallbackSrc }: AvatarProps) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={cn('shrink-0 rounded-full object-cover', AVATAR_SIZES[size], className)}
      />
    )
  }

  /*
   | تصویر پیش‌فرض سه‌بعدی — نیم‌تنه‌ی وسط‌چین روی پس‌زمینه شفاف.
   |
   | ⚠️ فایل مبدأ باید از پیش «trim» شده باشد. تصویر خام حدود دوسوم بوم
   | را حاشیه شفاف دارد؛ بدون برش، آدمک آن‌قدر کوچک می‌شود که در اندازه
   | xs/sm — یعنی همان اندازه‌ای که نوار بالا استفاده می‌کند — صورت
   | خوانده نمی‌شود. برای همین avatar-default.png جدا ساخته شده است.
   |
   | `object-contain` تا شانه‌ها از دو طرف بریده نشود؛ سوژه عمداً کمی
   | پایین‌تر از مرکز دایره می‌نشیند چون نیم‌تنه است نه سرِ تنها.
   |
   | `alt` عمداً خالی است: نام کاربر همان کنارش نوشته شده و اینجا تصویر
   | تزئینی است. خواندن دوباره نام توسط صفحه‌خوان اضافه‌گویی است.
   */
  if (fallbackSrc) {
    return (
      <span
        title={name}
        className={cn(
          'flex shrink-0 items-center justify-center overflow-hidden rounded-full',
          'border border-[var(--pf-border)] bg-[var(--pf-surface-muted)]',
          AVATAR_SIZES[size],
          className,
        )}
      >
        <img
          src={fallbackSrc}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="size-full object-contain"
        />
      </span>
    )
  }

  const hue = hueFromName(name)

  return (
    <span
      aria-label={name}
      title={name}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-[var(--pf-weight-semibold)] select-none',
        AVATAR_SIZES[size],
        className,
      )}
      style={{
        backgroundColor: `oklch(0.92 0.05 ${hue})`,
        color: `oklch(0.42 0.12 ${hue})`,
      }}
    >
      {initials}
    </span>
  )
}
